import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ProgressGraph from '../components/ProgressGraph';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  BookmarkIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  LockClosedIcon,
  MagnifyingGlassPlusIcon,
  PlayIcon,
  TrashIcon,
  TrophyIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AppPage, BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, Eyebrow, HeroPanel, Pill, Segmented } from '../components/UI/AppUI';

const mediaUrl = (path) => (!path ? null : path.startsWith('http') ? path : `${process.env.REACT_APP_API_URL || 'https://api.air-brief.com'}${path}`);
const pct = (n, d) => (d > 0 ? (n / d) * 100 : 0);

// Correct / incorrect / unseen split as one bar
const SplitBar = ({ total, correct, attempted, className = 'h-2' }) => {
  const incorrect = Math.max(0, attempted - correct);
  return (
    <span className={`flex w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10 ${className}`}>
      <span className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct(correct, total)}%` }} />
      <span className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${pct(incorrect, total)}%` }} />
    </span>
  );
};

// Large completion ring for the banner
const BigRing = ({ value }) => {
  const r = 42;
  const c = 2 * Math.PI * r;
  return (
    <span className="relative flex h-24 w-24 flex-shrink-0 items-center justify-center sm:h-28 sm:w-28">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="7" />
        <circle cx="50" cy="50" r={r} fill="none" strokeWidth="7" strokeLinecap="round"
          className={value >= 75 ? 'stroke-emerald-400' : 'stroke-primary-400'}
          strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, value) / 100)} style={{ transition: 'stroke-dashoffset .6s' }} />
      </svg>
      <span className="relative text-center">
        <span className="block text-2xl font-semibold tabular-nums text-white">{Math.round(value)}%</span>
        <span className="block text-[11px] uppercase tracking-wider text-gray-400">complete</span>
      </span>
    </span>
  );
};

// iOS-style switch row
const Toggle = ({ label, hint, checked, onChange, tone = 'primary', disabled }) => {
  const on = { primary: 'bg-primary-600', green: 'bg-emerald-600', red: 'bg-rose-600' }[tone];
  return (
    <label className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.04] ${disabled ? 'pointer-events-none opacity-40' : ''}`}>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-800 dark:text-gray-200">{label}</span>
        {hint && <span className="block text-xs text-slate-500 dark:text-gray-500">{hint}</span>}
      </span>
      <input type="checkbox" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/50 ${checked ? on : 'bg-slate-300 dark:bg-white/15'}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
      </span>
    </label>
  );
};

const Checkbox = (props) => (
  <input type="checkbox" {...props} className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-slate-300 bg-white text-primary-600 focus:ring-primary-500/40 dark:border-white/20 dark:bg-white/5" />
);

const Dialog = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
    <div role="dialog" aria-modal="true" aria-label={title} className={`${CARD} w-full max-w-md bg-white p-6 shadow-2xl dark:bg-slate-900`} onClick={(e) => e.stopPropagation()}>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
      {children}
    </div>
  </div>
);

// One chapter with its topics
const ChapterRow = ({ chapter, isSelected, onToggle, progress, expandedByDefault, selectedTopics, onTopicToggle }) => {
  const [expanded, setExpanded] = useState(expandedByDefault);
  const total = progress?.total_questions || chapter.topics?.reduce((n, t) => n + (t.question_count || 0), 0) || 0;
  const correct = progress?.correct_answers || 0;
  const attempted = progress?.attempted_questions || 0;
  const picked = chapter.topics?.filter((t) => selectedTopics.includes(t.id)).length || 0;
  const partial = isSelected && chapter.topics?.length > 0 && picked < chapter.topics.length;

  return (
    <li>
      <div className={`flex items-center gap-3 px-4 py-3.5 transition-colors sm:px-5 ${isSelected ? '' : 'opacity-60'}`}>
        <Checkbox checked={isSelected} onChange={onToggle} aria-label={`Include ${chapter.name}`}
          ref={(el) => { if (el) el.indeterminate = partial; }} />
        <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} className="group min-w-0 flex-1 text-left">
          <span className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-1.5">
              <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
              <span className="truncate text-sm font-semibold text-slate-900 group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-300">{chapter.name}</span>
            </span>
            <span className="flex-shrink-0 text-xs tabular-nums text-slate-500 dark:text-gray-400">
              <span className="font-semibold text-slate-700 dark:text-gray-200">{correct}</span> / {total}
              {partial && <span className="ml-2 text-primary-600 dark:text-primary-300">{picked} of {chapter.topics.length} topics</span>}
            </span>
          </span>
          <span className="mt-2 block pl-5"><SplitBar total={total} correct={correct} attempted={attempted} className="h-1.5" /></span>
        </button>
      </div>
      {expanded && chapter.topics?.length > 0 && (
        <ul className="mb-2 ml-11 mr-4 space-y-0.5 border-l border-slate-200 pl-3 sm:mr-5 dark:border-white/10">
          {chapter.topics.map((topic) => {
            const tp = progress?.topics_progress?.find((x) => x.topic_id === topic.id);
            const tTotal = tp?.total_questions || topic.question_count || 0;
            const tCorrect = tp?.correct_answers || 0;
            return (
              <li key={topic.id}>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50 dark:hover:bg-white/[0.04]">
                  <Checkbox checked={selectedTopics.includes(topic.id)} onChange={() => onTopicToggle(topic.id)} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm text-slate-700 dark:text-gray-300">{topic.name}</span>
                      <span className="flex-shrink-0 text-xs tabular-nums text-slate-400 dark:text-gray-500">{tCorrect} / {tTotal}</span>
                    </span>
                    <span className="mt-1.5 block"><SplitBar total={tTotal} correct={tCorrect} attempted={tp?.attempted_questions || 0} className="h-1" /></span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

// A question with its options; reveal highlights the correct one
const QuestionCard = ({ question, reveal, onZoom, footer, header }) => {
  const answers = [...(question.answers || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const image = question.annex_image;
  return (
    <article className={`${CARD} overflow-hidden`}>
      {header && <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-3 text-xs text-slate-500 dark:border-white/10 dark:text-gray-400">{header}</div>}
      <div className={`grid gap-5 p-5 ${image ? 'lg:grid-cols-[1fr_320px]' : ''}`}>
        <div className="min-w-0">
          <p className="whitespace-pre-line leading-relaxed text-slate-900 dark:text-white">{question.question_text}</p>
          <div className="mt-4 space-y-2">
            {question.question_type === 'USER_INPUT' ? (
              <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm ${reveal ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-slate-200 dark:border-white/10'}`}>
                <span className="font-semibold text-slate-500 dark:text-gray-400">Answer</span>
                <span className="flex-1 text-slate-900 dark:text-white">{reveal ? (question.correct_answer_text || 'N/A') : 'Hidden'}</span>
                {reveal && <CheckIcon className="h-5 w-5 text-emerald-500" />}
              </div>
            ) : answers.length ? answers.map((a, i) => {
              const right = reveal && a.is_correct;
              return (
                <div key={a.id} className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm ${right ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-slate-200 dark:border-white/10'}`}>
                  <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-xs font-bold ${right ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-gray-300'}`}>{String.fromCharCode(65 + i)}</span>
                  <span className={`flex-1 ${right ? 'font-medium text-emerald-800 dark:text-emerald-200' : 'text-slate-700 dark:text-gray-300'}`}>{a.answer_text}</span>
                  {right && <CheckIcon className="h-5 w-5 flex-shrink-0 text-emerald-500" />}
                </div>
              );
            }) : <p className="text-sm text-slate-500">No answer options available</p>}
          </div>
          {reveal && question.explanation && (
            <div className="mt-4 flex gap-3 rounded-xl bg-primary-500/10 p-4 text-sm">
              <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-primary-500" />
              <p className="text-slate-700 dark:text-gray-300"><span className="font-semibold text-slate-900 dark:text-white">Explanation. </span>{question.explanation}</p>
            </div>
          )}
        </div>
        {image && (
          <button type="button" onClick={() => onZoom(image)} className="group relative self-start overflow-hidden rounded-xl border border-slate-200 dark:border-white/10" aria-label="Enlarge image">
            <img src={image} alt="" className="w-full transition-transform group-hover:scale-[1.02]" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
            <span className="absolute right-2 top-2 rounded-lg bg-slate-950/70 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"><MagnifyingGlassPlusIcon className="h-4 w-4" /></span>
          </button>
        )}
      </div>
      {footer && <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-5 py-3 text-xs text-slate-500 dark:border-white/10 dark:text-gray-400">{footer}</div>}
    </article>
  );
};

const StatTile = ({ icon: Icon, label, value, hint, tone = 'text-primary-600 dark:text-primary-300' }) => (
  <div className={`${CARD} p-5`}>
    <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400"><Icon className={`h-4 w-4 ${tone}`} />{label}</span>
    <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500 dark:text-gray-500">{hint}</p>}
  </div>
);

const TABS = [
  { value: 'new-exam', label: 'Build a test', icon: AcademicCapIcon },
  { value: 'marked-questions', label: 'Marked', icon: BookmarkIcon },
  { value: 'similar-questions', label: 'Similar questions', icon: DocumentDuplicateIcon },
  { value: 'progress-metrics', label: 'Progress', icon: ChartBarIcon },
];

const Subject2 = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [subjectData, setSubjectData] = useState(null);
  const [subjectStructure, setSubjectStructure] = useState(null);
  const [, setMarkedQuestions] = useState(null);
  const [testParameters, setTestParameters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setBannerImageLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('new-exam');
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [resetOptions, setResetOptions] = useState({
    removeMarkedQuestions: false
  });
  const [gameAccess, setGameAccess] = useState(null); // null = not checked, true/false = checked
  
  // Marked Questions state
  const [markedQuestionsData, setMarkedQuestionsData] = useState([]);
  const [markedQuestionsLoading, setMarkedQuestionsLoading] = useState(false);
  const [markedQuestionsPage, setMarkedQuestionsPage] = useState(1);
  const [markedQuestionsHasMore, setMarkedQuestionsHasMore] = useState(true);
  const [markedQuestionsFilters, setMarkedQuestionsFilters] = useState({
    selectedMarking: 'all' // 'all', 'easy', 'difficult'
  });
  const [showAnswers, setShowAnswers] = useState(true);
  const [expandedImage, setExpandedImage] = useState(null);
  
  // Similar Questions state
  const [similarQuestionsData, setSimilarQuestionsData] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [similarQuestionsLoading, setSimilarQuestionsLoading] = useState(false);
  
  // Progress Metrics state
  const [progressMetrics, setProgressMetrics] = useState(null);
  const [progressMetricsLoading, setProgressMetricsLoading] = useState(false);
  const [questionCounts, setQuestionCounts] = useState({
    all: 0,
    easy: 0,
    difficult: 0
  });


  // Auto-fetch when marked questions tab is active
  useEffect(() => {
    if (activeTab === 'marked-questions') {
      fetchMarkedQuestionsData(1, true);
    }
  }, [activeTab]);

  // Auto-fetch when similar questions tab is active
  useEffect(() => {
    if (activeTab === 'similar-questions') {
      fetchSimilarQuestionsData();
    }
  }, [activeTab, subjectId]);

  // Auto-fetch progress metrics when tab becomes active
  useEffect(() => {
    if (activeTab === 'progress-metrics') {
      fetchProgressMetrics();
    }
  }, [activeTab, subjectId]);
  
  // Exam generation state
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [examMode, setExamMode] = useState('practice'); // 'practice' or 'real_test'
  const [questionCount, setQuestionCount] = useState(20);
  const [difficulty, setDifficulty] = useState('all');
  const [questionTypes, setQuestionTypes] = useState(['all']);
  const [includeUnseen, setIncludeUnseen] = useState(true);
  const [includeIncorrect, setIncludeIncorrect] = useState(false);
  const [includeWithImages, setIncludeWithImages] = useState(false);
  const [includeUserInputOnly, setIncludeUserInputOnly] = useState(false);
  const [markedEasy, setMarkedEasy] = useState(false);
  const [markedDifficult, setMarkedDifficult] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [maxQuestions, setMaxQuestions] = useState(1);
  const [creatingExam, setCreatingExam] = useState(false);

  useEffect(() => {
    if (subjectId) {
      fetchSubjectData();
      fetchSubjectStructure();
      fetchMarkedQuestions();
      fetchTestParameters();
    }
  }, [subjectId]);

  // Check game access when subject data is loaded and it's a game subject
  useEffect(() => {
    const checkGameAccess = async () => {
      const subject = subjectData?.subject;
      if (subject?.subject_type === 'game' && subject?.game_identifier) {
        const gameType = subject.game_identifier.toUpperCase();
        if (['SKT', 'MIC', 'MEK', 'RMS', 'ROT', 'OWT', 'PPT'].includes(gameType)) {
          try {
            const token = localStorage.getItem('atpl_access_token');
            const response = await fetch(`${API_URL}/api/payments/game-access/${gameType}/`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const data = await response.json();
              setGameAccess(data.has_access);
            } else {
              setGameAccess(false);
            }
          } catch (error) {
            setGameAccess(false);
          }
        }
      }
    };
    
    if (subjectData?.subject) {
      checkGameAccess();
    }
  }, [subjectData?.subject, API_URL]);

  // Preload banner image when subject data is available
  useEffect(() => {
    if (subjectData?.subject?.banner_image) {
      const imageUrl = subjectData.subject.banner_image.startsWith('http') 
        ? subjectData.subject.banner_image 
        : `${process.env.REACT_APP_API_URL || 'https://api.air-brief.com'}${subjectData.subject.banner_image}`;
      
      const img = new Image();
      img.onload = () => {
        setBannerImageLoaded(true);
      };
      img.onerror = () => {
        setBannerImageLoaded(false);
      };
      img.src = imageUrl;
    }
  }, [subjectData?.subject?.banner_image]);

  // Start with all chapters and topics selected by default
  useEffect(() => {
    if (subjectStructure?.chapters) {
      const allChapterIds = subjectStructure.chapters.map(chapter => chapter.id);
      setSelectedChapters(allChapterIds);
      
      // Also select all topics
      const allTopicIds = [];
      subjectStructure.chapters.forEach(chapter => {
        if (chapter.topics) {
          chapter.topics.forEach(topic => {
            allTopicIds.push(topic.id);
          });
        }
      });
      setSelectedTopics(allTopicIds);
    }
  }, [subjectStructure]);

// Update question count when selections or filters change
  useEffect(() => {
    const fetchAvailableQuestions = async () => {
      if (!subjectId) {
        setMaxQuestions(1);
        return;
      }

      try {
        const token = localStorage.getItem('atpl_access_token');
        const response = await fetch(`${API_URL}/api/exams/available-questions-count/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subject_id: subjectId,
            selected_chapters: selectedChapters,
            selected_topics: selectedTopics,
            include_unseen: includeUnseen,
            include_incorrect: includeIncorrect,
            include_with_images: includeWithImages,
            include_user_input_only: includeUserInputOnly,
            include_marked_difficult: markedDifficult,
            include_marked_easy: markedEasy,
            tags_filter: selectedTags
          })
        });

        if (response.ok) {
          const data = await response.json();
          setMaxQuestions(Math.max(data.available_questions, 1));
        } else {
          // Fallback to 1 if API fails
          setMaxQuestions(1);
        }
      } catch (error) {
        console.error('Failed to fetch available questions count:', error);
        setMaxQuestions(1);
      }
    };

    fetchAvailableQuestions();
  }, [subjectId, selectedChapters, selectedTopics, includeUnseen, includeIncorrect, includeWithImages, includeUserInputOnly, markedDifficult, markedEasy, selectedTags, API_URL]);

  // Set questionCount to maxQuestions when maxQuestions changes
  useEffect(() => {
    if (maxQuestions > 0) {
      setQuestionCount(maxQuestions);
    }
  }, [maxQuestions]);

  // Auto-disable "Unseen questions" filter when no unseen questions remain
  useEffect(() => {
    if (subjectData?.statistics) {
      const { total_questions, attempted_questions } = subjectData.statistics;
      const unseenQuestions = (total_questions || 0) - (attempted_questions || 0);
      
      // If no unseen questions and filter is ON, turn it OFF
      if (unseenQuestions <= 0 && includeUnseen) {
        setIncludeUnseen(false);
      }
    }
  }, [subjectData?.statistics]);

  const fetchSubjectData = async () => {
    try {
      const token = localStorage.getItem('atpl_access_token');
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/progress/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubjectData(data);
      }
    } catch (error) {
      // Error fetching subject data
    }
  };

  const fetchSubjectStructure = async () => {
    try {
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/structure/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubjectStructure(data);
      }
    } catch (error) {
      // Error fetching subject structure
    }
  };

  const fetchMarkedQuestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/marked-questions/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMarkedQuestions(data);
      }
    } catch (error) {
      // Error fetching marked questions
    }
  };

  const fetchTestParameters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams/subjects/${subjectId}/test-parameters/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTestParameters(data);
        if (data.total_questions) {
          setQuestionCount(Math.min(20, data.total_questions));
        }
      }
    } catch (error) {
      // Error fetching test parameters
    } finally {
      setLoading(false);
    }
  };

  const handleResetProgress = async () => {
    try {
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/reset-progress/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          remove_marked_questions: resetOptions.removeMarkedQuestions
        })
      });
      
      if (response.ok) {
        // Refresh the data
        fetchSubjectData();
        fetchSubjectStructure();
        if (resetOptions.removeMarkedQuestions) {
          fetchMarkedQuestions();
        }
        setShowResetConfirmation(false);
        setResetOptions({ removeMarkedQuestions: false });
      }
    } catch (error) {
      // Error resetting progress
    }
  };

  const fetchMarkedQuestionsData = async (page = 1, resetData = false, filterOverride = null) => {
    try {
      setMarkedQuestionsLoading(true);
      
      // Use filterOverride if provided, otherwise use current state
      const currentFilter = filterOverride || markedQuestionsFilters.selectedMarking;
      
      // Build markings parameter based on selection (server-side filtering)
      const markingsParam = currentFilter === 'all' 
        ? 'easy,difficult' 
        : currentFilter;
        
      const params = new URLSearchParams({
        page: page.toString(),
        markings: markingsParam
      });


      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/marked-questions/?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Handle the nested marked_questions structure
        let questionsArray = [];
        if (data.marked_questions) {
          const markedQuestions = data.marked_questions;
          let allQuestions = []; // Use let instead of const
          
          // Calculate question counts
          const counts = {
            easy: markedQuestions.easy ? markedQuestions.easy.length : 0,
            difficult: markedQuestions.difficult ? markedQuestions.difficult.length : 0
          };
          counts.all = counts.easy + counts.difficult;
          setQuestionCounts(counts);
          
          // Clear the array and add questions based on selected marking
          allQuestions = [];
          
          if (currentFilter === 'all') {
            // Show all questions
            if (markedQuestions.easy && Array.isArray(markedQuestions.easy)) {
              markedQuestions.easy.forEach(q => {
                allQuestions.push({ ...q, markings: ['easy'] });
              });
            }
            if (markedQuestions.difficult && Array.isArray(markedQuestions.difficult)) {
              markedQuestions.difficult.forEach(q => {
                allQuestions.push({ ...q, markings: ['difficult'] });
              });
            }
          } else {
            // Show only selected marking type
            const selectedType = currentFilter;
            if (markedQuestions[selectedType] && Array.isArray(markedQuestions[selectedType])) {
              markedQuestions[selectedType].forEach(q => {
                allQuestions.push({ ...q, markings: [selectedType] });
              });
            }
          }
          
          questionsArray = allQuestions;
        } else if (Array.isArray(data.results)) {
          questionsArray = data.results;
        } else if (Array.isArray(data)) {
          questionsArray = data;
        }
        
        
        if (resetData || page === 1) {
          setMarkedQuestionsData(questionsArray);
        } else {
          setMarkedQuestionsData(prev => [...prev, ...questionsArray]);
        }
        
        setMarkedQuestionsHasMore(!!data.next);
        setMarkedQuestionsPage(page);
      }
    } catch (error) {
      // Error fetching marked questions
    } finally {
      setMarkedQuestionsLoading(false);
    }
  };

  // Unmark a question (remove from marked list)
  const handleUnmarkQuestion = async (questionId, currentMarkingType, questionNumber) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to remove Question ${questionNumber || questionId} from your marked questions?`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/exams/questions/${questionId}/mark/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          marking_type: currentMarkingType // Send the same marking type to toggle it off
        })
      });

      if (response.ok) {
        // Remove the question from the local state
        setMarkedQuestionsData(prev => 
          prev.filter(q => q.id !== questionId)
        );
        
        // Update question counts
        setQuestionCounts(prev => ({
          ...prev,
          [currentMarkingType]: Math.max(0, prev[currentMarkingType] - 1),
          all: Math.max(0, prev.all - 1)
        }));
        
        toast.success(`Question ${questionNumber || questionId} removed from marked questions`);
      } else {
        toast.error('Failed to unmark question. Please try again.');
      }
    } catch (error) {
      toast.error('Error occurred while unmarking question. Please try again.');
    }
  };

  const handleChapterToggle = (chapterId) => {
    const isCurrentlySelected = selectedChapters.includes(chapterId);
    
    if (isCurrentlySelected) {
      // Deselecting chapter - remove chapter and all its topics
      setSelectedChapters(prev => prev.filter(id => id !== chapterId));
      
      // Remove all topics from this chapter
      const chapter = subjectStructure?.chapters?.find(c => c.id === chapterId);
      if (chapter?.topics) {
        const topicIds = chapter.topics.map(t => t.id);
        setSelectedTopics(prev => prev.filter(id => !topicIds.includes(id)));
      }
    } else {
      // Selecting chapter - add chapter and all its topics
      setSelectedChapters(prev => [...prev, chapterId]);
      
      // Add all topics from this chapter
      const chapter = subjectStructure?.chapters?.find(c => c.id === chapterId);
      if (chapter?.topics) {
        const topicIds = chapter.topics.map(t => t.id);
        setSelectedTopics(prev => [...new Set([...prev, ...topicIds])]);
      }
    }
  };

  const handleSelectAllChapters = () => {
    if (subjectStructure?.chapters) {
      const allChapterIds = subjectStructure.chapters.map(chapter => chapter.id);
      setSelectedChapters(allChapterIds);
      
      // Also select all topics
      const allTopicIds = [];
      subjectStructure.chapters.forEach(chapter => {
        if (chapter.topics) {
          chapter.topics.forEach(topic => {
            allTopicIds.push(topic.id);
          });
        }
      });
      setSelectedTopics(allTopicIds);
    }
  };

  const handleDeselectAllChapters = () => {
    setSelectedChapters([]);
    setSelectedTopics([]); // Also clear all selected topics
  };

  const handleTopicToggle = (topicId) => {
    const isCurrentlySelected = selectedTopics.includes(topicId);
    
    // Find which chapter this topic belongs to
    let parentChapter = null;
    for (const chapter of subjectStructure?.chapters || []) {
      if (chapter.topics?.some(t => t.id === topicId)) {
        parentChapter = chapter;
        break;
      }
    }
    
    if (isCurrentlySelected) {
      // Deselecting topic
      const newSelectedTopics = selectedTopics.filter(id => id !== topicId);
      setSelectedTopics(newSelectedTopics);
      
      // Check if all topics in the parent chapter are now deselected
      if (parentChapter) {
        const chapterTopicIds = parentChapter.topics.map(t => t.id);
        const remainingSelectedInChapter = newSelectedTopics.filter(id => chapterTopicIds.includes(id));
        
        // If no topics remain selected in this chapter, deselect the chapter too
        if (remainingSelectedInChapter.length === 0) {
          setSelectedChapters(prev => prev.filter(id => id !== parentChapter.id));
        }
      }
    } else {
      // Selecting topic - also select the parent chapter if not already selected
      setSelectedTopics(prev => [...prev, topicId]);
      
      if (parentChapter && !selectedChapters.includes(parentChapter.id)) {
        setSelectedChapters(prev => [...prev, parentChapter.id]);
      }
    }
  };


  const handleRetestClick = async (session) => {
    // Directly create retest with the same mode as the original session
    try {
      const response = await fetch(`${API_URL}/api/exams/sessions/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          subject: subjectId,
          mode: session.mode, // Use the same mode as the original session
          total_questions: session.total_questions,
          retest_session_id: session.id // Include original session ID for reference
        })
      });

      if (response.ok) {
        const newSession = await response.json();
        toast.success('Retest exam created successfully!');
        navigate(`/exam/${newSession.id}`);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to create retest exam.');
      }
    } catch (error) {
      toast.error('Error creating retest exam.');
    }
  };


  const getMaxQuestions = () => {
    if (!subjectStructure?.chapters) return 1;
    
    let totalQuestions = 0;
    
    // If no chapters or topics selected, return total available
    if (selectedChapters.length === 0 && selectedTopics.length === 0) {
      return subjectStructure.chapters.reduce((sum, chapter) => {
        return sum + (chapter.topics?.reduce((topicSum, topic) => topicSum + (topic.question_count || 0), 0) || 0);
      }, 0) || 1;
    }
    
    // Count questions based on selected topics only
    // For each selected chapter, check if ALL its topics are selected
    selectedChapters.forEach(chapterId => {
      const chapter = subjectStructure.chapters.find(c => c.id === chapterId);
      if (!chapter?.topics) return;
      
      const chapterTopicIds = chapter.topics.map(t => t.id);
      const selectedTopicsInChapter = chapterTopicIds.filter(id => selectedTopics.includes(id));
      
      // Check if all topics in this chapter are selected
      if (selectedTopicsInChapter.length === chapterTopicIds.length) {
        // All topics selected - count whole chapter
        totalQuestions += chapter.topics.reduce((sum, topic) => sum + (topic.question_count || 0), 0);
      } else {
        // Only some topics selected - count only selected topics
        selectedTopicsInChapter.forEach(topicId => {
          const topic = chapter.topics.find(t => t.id === topicId);
          if (topic) {
            totalQuestions += topic.question_count || 0;
          }
        });
      }
    });
    
    // Also count topics from chapters that are not selected (edge case)
    selectedTopics.forEach(topicId => {
      for (const chapter of subjectStructure.chapters) {
        if (!selectedChapters.includes(chapter.id) && chapter.topics) {
          const topic = chapter.topics.find(t => t.id === topicId);
          if (topic) {
            totalQuestions += topic.question_count || 0;
            break;
          }
        }
      }
    });
    
    return Math.max(totalQuestions, 1);
  };

  const createExam = async () => {
    if (examMode === 'practice' && selectedChapters.length === 0) {
      toast.error('Please select at least one chapter');
      return;
    }

    setCreatingExam(true);
    try {
      const examData = {
        subject: subjectId,
        mode: examMode,
        title: `${subjectData?.name || 'Subject'} - ${examMode === 'practice' ? 'Practice' : 'Real Test'}`,
        total_questions: examMode === 'real_test' ? (testParameters?.total_questions || questionCount) : questionCount,
        selected_chapters: examMode === 'real_test' ? [] : selectedChapters, // Real test uses all chapters
        selected_topics: examMode === 'real_test' ? [] : selectedTopics, // Real test uses all topics
        include_incorrect_only: examMode === 'real_test' ? false : includeIncorrect,
        include_unseen_only: examMode === 'real_test' ? false : includeUnseen,
        include_with_images: examMode === 'real_test' ? false : includeWithImages,
        include_user_input_only: examMode === 'real_test' ? false : includeUserInputOnly,
        include_marked_difficult: examMode === 'real_test' ? false : markedDifficult,
        include_marked_easy: examMode === 'real_test' ? false : markedEasy,
        include_pinned_only: false,
        tags_filter: examMode === 'real_test' ? [] : selectedTags, // Real test ignores tag filters
        // For practice mode, no time limit or passing score needed
        time_limit_minutes: examMode === 'real_test' ? testParameters?.time_limit_minutes : null,
        passing_score: examMode === 'real_test' ? testParameters?.passing_score : 75
      };

      const response = await fetch(`${API_URL}/api/exams/sessions/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify(examData)
      });

      if (response.ok) {
        const exam = await response.json();
        navigate(`/exam/${exam.exam_session_id}`);
      } else {
        const error = await response.json();
        toast.error(error.error || error.detail || 'Failed to create exam');
      }
    } catch (error) {
      toast.error('Network error occurred');
    } finally {
      setCreatingExam(false);
    }
  };

  // Fetch similar questions data
  const fetchSimilarQuestionsData = async () => {
    try {
      setSimilarQuestionsLoading(true);
      
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/similar-questions/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSimilarQuestionsData(data.concepts || []);
        // Auto-select first concept if available
        if (data.concepts && data.concepts.length > 0) {
          setSelectedConcept(data.concepts[0]);
          setCurrentQuestionIndex(0);
        }
      } else {
        setSimilarQuestionsData([]);
      }
    } catch (error) {
    } finally {
      setSimilarQuestionsLoading(false);
    }
  };

  const fetchProgressMetrics = async () => {
    setProgressMetricsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/progress-metrics/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProgressMetrics(data);
      } else {
        setProgressMetrics(null);
      }
    } catch (error) {
      setProgressMetrics(null);
    } finally {
      setProgressMetricsLoading(false);
    }
  };

  if (loading) {
    return (
      <AppPage wide>
        <div className="h-48 animate-pulse rounded-3xl bg-slate-200 dark:bg-white/5" />
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="h-80 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" />
          <div className="h-80 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" />
        </div>
      </AppPage>
    );
  }

  const { subject, statistics, chapters_progress } = subjectData || {};
  const total = statistics?.total_questions || 0;
  const correct = statistics?.correct_answers || 0;
  const attempted = statistics?.attempted_questions || 0;
  const unseen = Math.max(0, total - attempted);
  const completion = pct(correct, total);
  const accuracy = attempted ? Math.round(pct(correct, attempted)) : null;
  const chapters = subjectStructure?.chapters || [];
  const allSelected = chapters.length > 0 && selectedChapters.length === chapters.length;
  const realTest = examMode === 'real_test';
  const testSize = realTest ? (testParameters?.total_questions || questionCount) : questionCount;
  const canStart = !creatingExam && (realTest || selectedChapters.length > 0);

  const banner = (
    <HeroPanel image={mediaUrl(subject?.banner_image || subject?.image)}>
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-white">
        <ArrowLeftIcon className="h-4 w-4" />Dashboard
      </Link>
      <div className="mt-4 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 max-w-2xl">
          {subject?.code && <span className="inline-flex rounded-md bg-white/10 px-2 py-0.5 text-xs font-semibold tracking-wider text-white ring-1 ring-white/15 backdrop-blur">{subject.code}</span>}
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-4xl">{subject?.name?.replace(/^(DLR|ATPL)\s*-\s*/i, '')}</h1>
          {subject?.description && <p className="mt-2 line-clamp-2 text-sm text-gray-300 sm:text-base">{subject.description}</p>}
        </div>
        {total > 0 && (
          <div className="flex items-center gap-5">
            <BigRing value={completion} />
            <dl className="space-y-2 text-sm">
              <div><dt className="inline text-gray-400">Correct </dt><dd className="inline font-semibold tabular-nums text-white">{correct.toLocaleString()} / {total.toLocaleString()}</dd></div>
              <div><dt className="inline text-gray-400">Unseen </dt><dd className="inline font-semibold tabular-nums text-white">{unseen.toLocaleString()}</dd></div>
              <div><dt className="inline text-gray-400">Accuracy </dt><dd className="inline font-semibold tabular-nums text-white">{accuracy === null ? '–' : `${accuracy}%`}</dd></div>
            </dl>
          </div>
        )}
      </div>
      {total > 0 && (
        <div className="mt-6">
          <SplitBar total={total} correct={correct} attempted={attempted} className="h-1.5 !bg-white/10" />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />Correct</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-500" />Incorrect</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-white/20" />Unseen</span>
          </div>
        </div>
      )}
    </HeroPanel>
  );

  // Game subjects without access
  if (subject?.subject_type === 'game' && gameAccess === false) {
    return (
      <AppPage wide>
        {banner}
        <div className={`${CARD} mx-auto mt-6 max-w-lg`}>
          <Empty icon={LockClosedIcon} title="This game is locked"
            action={<Link to="/store" className={BTN_PRIMARY}>Unlock in the store</Link>}>
            Add {subject.game_identifier?.toUpperCase() || 'this game'} to your DLR plan to play it.
          </Empty>
        </div>
      </AppPage>
    );
  }

  return (
    <AppPage wide>
      {banner}

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label="Subject sections" className="-mx-3 flex max-w-[calc(100%+1.5rem)] gap-1 overflow-x-auto px-3 sm:mx-0 sm:max-w-full sm:px-0">
          {TABS.map(({ value, label, icon: Icon }) => {
            const active = activeTab === value;
            return (
              <button key={value} type="button" onClick={() => setActiveTab(value)} aria-current={active ? 'page' : undefined}
                className={`flex flex-shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${active
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                  : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            );
          })}
        </nav>
        <button type="button" onClick={() => setShowResetConfirmation(true)}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-rose-500/10 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400">
          <TrashIcon className="h-4 w-4" />Reset progress
        </button>
      </div>

      {/* Build a test */}
      {activeTab === 'new-exam' && (
        <div className="mt-5 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <section className={`${CARD} overflow-hidden`}>
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-5 dark:border-white/10">
              <label className="flex cursor-pointer items-center gap-3">
                <Checkbox checked={allSelected} onChange={(e) => (e.target.checked ? handleSelectAllChapters() : handleDeselectAllChapters())}
                  ref={(el) => { if (el) el.indeterminate = selectedChapters.length > 0 && !allSelected; }} />
                <span className="font-semibold text-slate-900 dark:text-white">Chapters</span>
              </label>
              <span className="text-sm text-slate-500 dark:text-gray-400">{selectedChapters.length} of {chapters.length} selected</span>
            </div>
            {chapters.length ? (
              <ul className="divide-y divide-slate-100 dark:divide-white/5">
                {chapters.map((chapter) => (
                  <ChapterRow key={chapter.id} chapter={chapter}
                    isSelected={selectedChapters.includes(chapter.id)}
                    onToggle={() => handleChapterToggle(chapter.id)}
                    progress={chapters_progress?.find((cp) => cp.chapter.id === chapter.id)}
                    expandedByDefault={chapters.length === 1}
                    selectedTopics={selectedTopics} onTopicToggle={handleTopicToggle} />
                ))}
              </ul>
            ) : <Empty icon={AcademicCapIcon} title="No chapters yet">Questions for this subject are on their way.</Empty>}
          </section>

          {/* Test builder */}
          <aside className={`${CARD} lg:sticky lg:top-20`}>
            <div className="p-5">
              <Eyebrow>Your test</Eyebrow>
              <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Test mode">
                {[
                  { value: 'practice', title: 'Practice', desc: 'Your filters, instant feedback', icon: PlayIcon },
                  { value: 'real_test', title: 'Real test', desc: 'Exam rules, timed', icon: ClockIcon },
                ].map(({ value, title, desc, icon: Icon }) => {
                  const on = examMode === value;
                  return (
                    <button key={value} type="button" role="radio" aria-checked={on} onClick={() => setExamMode(value)}
                      className={`rounded-xl border p-3 text-left transition-colors ${on
                        ? 'border-primary-500 bg-primary-500/10 ring-1 ring-primary-500'
                        : 'border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20'}`}>
                      <Icon className={`h-5 w-5 ${on ? 'text-primary-600 dark:text-primary-300' : 'text-slate-400'}`} />
                      <span className="mt-2 block text-sm font-semibold text-slate-900 dark:text-white">{title}</span>
                      <span className="block text-xs text-slate-500 dark:text-gray-400">{desc}</span>
                    </button>
                  );
                })}
              </div>

              {realTest ? (
                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  {[
                    ['Questions', testParameters?.total_questions ?? '–'],
                    ['Minutes', testParameters?.time_limit_minutes ?? '–'],
                    ['Pass mark', testParameters?.passing_score != null ? `${testParameters.passing_score}%` : '–'],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-xl bg-slate-50 px-2 py-3 dark:bg-white/[0.04]">
                      <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">{v}</p>
                      <p className="text-xs text-slate-500 dark:text-gray-400">{k}</p>
                    </div>
                  ))}
                  <p className="col-span-3 mt-1 text-left text-xs text-slate-500 dark:text-gray-400">Drawn from every chapter, like the real exam. Filters don't apply.</p>
                </div>
              ) : (
                <>
                  <div className="mt-6">
                    <div className="flex items-baseline justify-between">
                      <label htmlFor="q-count" className="text-sm font-semibold text-slate-900 dark:text-white">Questions</label>
                      <span className="text-xs text-slate-500 dark:text-gray-400">{maxQuestions.toLocaleString()} available</span>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <input id="q-count" type="number" min="1" max={maxQuestions} value={questionCount}
                        onChange={(e) => setQuestionCount(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), maxQuestions))}
                        className="w-24 rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm font-semibold tabular-nums text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-white/15 dark:bg-white/[0.04] dark:text-white" />
                      <div className="flex flex-1 flex-wrap gap-1.5">
                        {[10, 20, 50].filter((n) => n < maxQuestions).map((n) => (
                          <button key={n} type="button" onClick={() => setQuestionCount(n)} aria-pressed={questionCount === n}
                            className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${questionCount === n ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10'}`}>{n}</button>
                        ))}
                        <button type="button" onClick={() => setQuestionCount(maxQuestions)} aria-pressed={questionCount === maxQuestions}
                          className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${questionCount === maxQuestions ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10'}`}>All</button>
                      </div>
                    </div>
                  </div>

                  <p className="mt-6 text-sm font-semibold text-slate-900 dark:text-white">Include</p>
                  <div className="-mx-3 mt-1">
                    <Toggle label="Unseen questions" checked={includeUnseen} onChange={setIncludeUnseen} disabled={total > 0 && unseen === 0} hint={total > 0 && unseen === 0 ? "You've seen them all" : undefined} />
                    <Toggle label="Ones I got wrong" checked={includeIncorrect} onChange={setIncludeIncorrect} />
                    <Toggle label="Questions with images" checked={includeWithImages} onChange={setIncludeWithImages} />
                    <Toggle label="Typed-answer questions only" checked={includeUserInputOnly} onChange={setIncludeUserInputOnly} />
                  </div>
                  <p className="mt-4 text-sm font-semibold text-slate-900 dark:text-white">Marked by you</p>
                  <div className="-mx-3 mt-1">
                    <Toggle label="Marked easy" tone="green" checked={markedEasy} onChange={setMarkedEasy} />
                    <Toggle label="Marked difficult" tone="red" checked={markedDifficult} onChange={setMarkedDifficult} />
                  </div>
                </>
              )}
            </div>

            <div className="hidden border-t border-slate-200 p-5 md:block dark:border-white/10">
              <button type="button" onClick={createExam} disabled={!canStart} className={`${BTN_PRIMARY} w-full py-3 text-base`}>
                {creatingExam ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <PlayIcon className="h-5 w-5" />}
                {creatingExam ? 'Creating your test…' : `Start ${realTest ? 'real test' : 'practice'}`}
              </button>
              <p className="mt-2 text-center text-xs text-slate-500 dark:text-gray-400">
                {testSize} questions{realTest ? '' : ` · ${selectedChapters.length} chapter${selectedChapters.length === 1 ? '' : 's'}`}
              </p>
            </div>
          </aside>

          {/* Phones: start bar above the bottom tabs */}
          <div className="fixed inset-x-0 bottom-16 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden dark:border-white/10 dark:bg-slate-950/95">
            <div className="mx-auto flex max-w-xl items-center gap-3">
              <p className="min-w-0 flex-1 text-sm text-slate-600 dark:text-gray-300">
                <span className="font-semibold text-slate-900 dark:text-white">{testSize} questions</span>
                <span className="block truncate text-xs text-slate-500 dark:text-gray-400">{realTest ? 'Real test' : `Practice · ${selectedChapters.length} chapter${selectedChapters.length === 1 ? '' : 's'}`}</span>
              </p>
              <button type="button" onClick={createExam} disabled={!canStart} className={BTN_PRIMARY}>
                {creatingExam ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <PlayIcon className="h-5 w-5" />}Start
              </button>
            </div>
          </div>
          <div className="h-16 md:hidden" aria-hidden="true" />
        </div>
      )}

      {/* Marked questions */}
      {activeTab === 'marked-questions' && (
        <div className="mt-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Segmented label="Marking" value={markedQuestionsFilters.selectedMarking}
              options={[
                { value: 'all', label: `All (${questionCounts.all})` },
                { value: 'easy', label: `Easy (${questionCounts.easy})` },
                { value: 'difficult', label: `Difficult (${questionCounts.difficult})` },
              ]}
              onChange={(v) => { setMarkedQuestionsFilters((prev) => ({ ...prev, selectedMarking: v })); fetchMarkedQuestionsData(1, true, v); }} />
            <button type="button" onClick={() => setShowAnswers(!showAnswers)} className={`${BTN_SECONDARY} px-4 py-2`}>
              {showAnswers ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}{showAnswers ? 'Hide answers' : 'Show answers'}
            </button>
          </div>
          <div className="mt-4 space-y-4">
            {markedQuestionsLoading && markedQuestionsData.length === 0 ? (
              [0, 1].map((i) => <div key={i} className="h-48 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" />)
            ) : markedQuestionsData.length ? markedQuestionsData.map((q) => (
              <QuestionCard key={q.id} question={q} reveal={showAnswers} onZoom={setExpandedImage}
                header={<>
                  <span className="font-semibold text-slate-700 dark:text-gray-200">Question {q.question_number || q.id}</span>
                  <span className="flex items-center gap-2">
                    {(q.tags || []).map((t) => <Pill key={t} tone="blue">{t}</Pill>)}
                    <Pill tone={q.markings?.[0] === 'easy' ? 'green' : 'red'}>{q.markings?.[0] === 'easy' ? 'Easy' : 'Difficult'}</Pill>
                  </span>
                </>}
                footer={<>
                  <span>Attempts: {q.times_answered || 0}{q.marked_at ? ` · Marked ${new Date(q.marked_at).toLocaleDateString()}` : ''}</span>
                  <button type="button" onClick={() => handleUnmarkQuestion(q.id, q.markings[0], q.question_number)}
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-rose-600 hover:bg-rose-500/10 dark:text-rose-400">
                    <XMarkIcon className="h-4 w-4" />Unmark
                  </button>
                </>} />
            )) : (
              <div className={CARD}>
                <Empty icon={BookmarkIcon} title="No marked questions">While you practise, mark questions as easy or difficult and they'll collect here.</Empty>
              </div>
            )}
            {markedQuestionsHasMore && markedQuestionsData.length > 0 && (
              <div className="text-center">
                <button type="button" disabled={markedQuestionsLoading} onClick={() => !markedQuestionsLoading && fetchMarkedQuestionsData(markedQuestionsPage + 1, false)} className={BTN_SECONDARY}>
                  {markedQuestionsLoading ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Similar questions */}
      {activeTab === 'similar-questions' && (
        <div className="mt-5">
          {similarQuestionsLoading ? (
            <div className="h-80 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" />
          ) : !similarQuestionsData.length ? (
            <div className={CARD}><Empty icon={DocumentDuplicateIcon} title="No similar questions yet">Groups of questions that test the same idea will appear here.</Empty></div>
          ) : (
            <div className="grid items-start gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
              <section className={`${CARD} overflow-hidden`}>
                <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
                  <p className="font-semibold text-slate-900 dark:text-white">Concepts</p>
                  <p className="text-xs text-slate-500 dark:text-gray-400">{similarQuestionsData.length} groups</p>
                </div>
                <ul className="max-h-72 overflow-y-auto lg:max-h-[calc(100vh-260px)]">
                  {similarQuestionsData.map((c) => {
                    const on = selectedConcept?.id === c.id;
                    return (
                      <li key={c.id}>
                        <button type="button" onClick={() => { setSelectedConcept(c); setCurrentQuestionIndex(0); }} aria-current={on ? 'true' : undefined}
                          className={`block w-full border-l-2 px-5 py-3 text-left transition-colors ${on ? 'border-primary-500 bg-primary-500/10' : 'border-transparent hover:bg-slate-50 dark:hover:bg-white/[0.04]'}`}>
                          <span className="flex items-center justify-between gap-2">
                            <span className={`text-sm font-semibold ${on ? 'text-primary-700 dark:text-primary-300' : 'text-slate-900 dark:text-white'}`}>{c.identifier}</span>
                            <span className="text-xs text-slate-500 dark:text-gray-400">{c.questions?.length || 0}</span>
                          </span>
                          <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500 dark:text-gray-400">{c.title || c.description}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
              {selectedConcept && (() => {
                const qs = selectedConcept.questions || [];
                const q = qs[currentQuestionIndex];
                return (
                  <section>
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <Eyebrow>{selectedConcept.identifier}</Eyebrow>
                        <p className="mt-1 font-semibold text-slate-900 dark:text-white">{selectedConcept.title || selectedConcept.description}</p>
                      </div>
                      {qs.length > 1 && (
                        <div className="flex items-center gap-2">
                          <button type="button" aria-label="Previous question" disabled={currentQuestionIndex === 0} onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))} className={`${BTN_SECONDARY} px-3 py-2 disabled:opacity-40`}><ChevronLeftIcon className="h-4 w-4" /></button>
                          <span className="min-w-[4rem] text-center text-sm font-semibold tabular-nums text-slate-700 dark:text-gray-200">{currentQuestionIndex + 1} / {qs.length}</span>
                          <button type="button" aria-label="Next question" disabled={currentQuestionIndex >= qs.length - 1} onClick={() => setCurrentQuestionIndex(Math.min(qs.length - 1, currentQuestionIndex + 1))} className={`${BTN_SECONDARY} px-3 py-2 disabled:opacity-40`}><ChevronRightIcon className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                    {q && <QuestionCard question={q} reveal onZoom={setExpandedImage}
                      footer={q.difficulty ? <Pill tone={q.difficulty === 'easy' ? 'green' : q.difficulty === 'medium' ? 'amber' : 'red'}>{q.difficulty}</Pill> : null} />}
                  </section>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Progress */}
      {activeTab === 'progress-metrics' && (
        <div className="mt-5">
          {progressMetricsLoading ? (
            <div className="h-80 animate-pulse rounded-2xl bg-slate-200 dark:bg-white/5" />
          ) : !progressMetrics ? (
            <div className={CARD}><Empty icon={ChartBarIcon} title="No progress yet">Answer a few questions and your trends will show up here.</Empty></div>
          ) : (() => {
            const op = progressMetrics.overall_progress;
            const done = (progressMetrics.exam_sessions || []).filter((s) => s.status === 'completed' || s.status === 'submitted');
            const avg = done.length ? done.reduce((n, s) => n + (s.score || 0), 0) / done.length : 0;
            const mins = op.total_time_minutes || 0;
            return (
              <>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                  <StatTile icon={CheckCircleIcon} tone="text-emerald-500" label="Success rate" value={`${op.success_rate.toFixed(1)}%`} />
                  <StatTile icon={BookmarkIcon} label="Questions seen" value={progressMetrics.session_stats.unique_questions} hint={`of ${progressMetrics.subject.total_questions}`} />
                  <StatTile icon={ClockIcon} tone="text-violet-500" label="Study time" value={`${Math.floor(mins / 60)}h ${mins % 60}m`} />
                  <StatTile icon={TrophyIcon} tone="text-amber-500" label="Best score" value={`${op.best_score.toFixed(1)}%`} />
                  <StatTile icon={ChartBarIcon} tone="text-sky-500" label="Average score" value={`${avg.toFixed(1)}%`} hint={`${done.length} finished test${done.length === 1 ? '' : 's'}`} />
                </div>
                <section className={`${CARD} mt-6 p-5`}>
                  <h2 className="font-semibold text-slate-900 dark:text-white">Progress over time</h2>
                  <p className="mb-4 text-sm text-slate-500 dark:text-gray-400">Each point is a test. Pick one to retake it.</p>
                  <ProgressGraph subjectId={subjectId} onRetestClick={handleRetestClick} />
                </section>
              </>
            );
          })()}
        </div>
      )}

      {showResetConfirmation && (
        <Dialog title="Reset progress?" onClose={() => { setShowResetConfirmation(false); setResetOptions({ removeMarkedQuestions: false }); }}>
          <p className="mt-2 text-sm text-slate-600 dark:text-gray-400">All your answers for {subject?.name || 'this subject'} will be cleared. This can't be undone.</p>
          <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-slate-700 dark:text-gray-300">
            <Checkbox checked={resetOptions.removeMarkedQuestions} onChange={(e) => setResetOptions((p) => ({ ...p, removeMarkedQuestions: e.target.checked }))} />
            Also clear my easy / difficult marks
          </label>
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => { setShowResetConfirmation(false); setResetOptions({ removeMarkedQuestions: false }); }} className={BTN_SECONDARY}>Cancel</button>
            <button type="button" onClick={handleResetProgress} className={BTN_DANGER}>Reset progress</button>
          </div>
        </Dialog>
      )}

      {expandedImage && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/90 p-4" onClick={() => setExpandedImage(null)}>
          <img src={expandedImage} alt="Question figure, enlarged" className="max-h-[90vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
          <button type="button" onClick={() => setExpandedImage(null)} aria-label="Close" className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><XMarkIcon className="h-6 w-6" /></button>
        </div>
      )}
    </AppPage>
  );
};

export default Subject2;
